import Vue from 'vue';
import VueRouter from 'vue-router';
import {NavigationGuardNext, Route, RouteConfig} from 'vue-router/types/router';
import store from '@/store';
import {LoginApi} from '@/classes/api/login.api.class';
import {CheckLoginResponseDto} from '@/classes/dto/authenticator/response/CheckLogin.response.dto';
import {NotifierSocketApi} from '@/classes/api/notifier.socket.api';
import LoginView from '@/views/LoginView.vue';
import MissingPageView from '@/views/MissingPageView.vue';
import {InitializationApi} from '@/classes/api/initialization.api.class';
import {ClientHooks} from '@/hooks';
import {isAxiosError} from 'axios';
import {getPixelIdForPath} from "@/helpers/meta/metaTrack";
import {loadMetaPixel} from "@/helpers/meta/metaConsent";
import {resolveRouteAccess} from '@/router/routeAccess';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {trackRecentVisit} from '@/components/app/search/searchStore';
import {EMPLOYEE_ROUTES} from '@/router/employeeRoutes';
import {STATS_ROUTES} from '@/router/statsRoutes';
import {ACQUISITION_ROUTES} from '@/router/acquisitionRoutes';
import {SETTINGS_ROUTES} from '@/router/settingsRoutes';
import {OPTIMIZATIONS_ENABLED} from '@/helpers/featureFlags';
import {takeoverOfferRoutes} from '@/router/takeoverOfferRoutes';
import {makeupRoundRoutes} from '@/router/makeupRoundRoutes';
import {qualityFeedbackRoutes} from '@/router/qualityFeedbackRoutes';
import {applicantDeletionRoutes} from '@/router/applicantDeletionRoutes';
import {APPLICANT_PORTAL_ROUTES, TEACHER_PORTAL_ROUTES} from '@/router/teacherPortalRoutes';
import {isApplicantPortalPath, isNoAccessPath, isTeacherPortalPath, NO_ACCESS_HOME} from '@/helpers/portalAccess.helper';
import {consumeAccountSwitch, routeTitle} from '@/helpers/accountSwitch.helper';
import {applyRobotsMeta} from '@/helpers/seo.helper';
import {AUTH_ROUTES} from '@/router/authRoutes';
import {testLinkTarget} from '@/helpers/whatsAppTemplates/waExampleLinks';


Vue.use(VueRouter);

const routes: RouteConfig[] = [
    {
        path: '/courseRepairKit',
        name: 'Kursreparaturkit',
        component: () => import(/* webpackChunkName: "course-repair" */ '../views/private/CourseRepairKit.vue'),
    },
    {
        path: '/',
        name: 'Dashboard',
        component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
    },
    {
        path: '/demo',
        name: 'Demo der neuesten Funktionen in Vanilla',
        component: () => import(/* webpackChunkName: "demo" */ '../views/demoView.vue'),
    },
    {
        path: '/terminations',
        name: 'Kündigungen',
        component: () => import(/* webpackChunkName: "terminations" */ '../views/Terminations.vue'),
    },
    // {
    //   path: '/announcements',
    //   name: 'Ankündigungen',
    //   component: () => import('../views/Announcements.vue'),
    // },
    {
        path: '/today/',
        name: 'Heute',
        component: () => import(/* webpackChunkName: "my-schedule" */ '../views/TodayView.vue'),
    },
    {
        path: '/schedule/:calendarDate?',
        name: 'Mein Stundenplan',
        component: () => import(/* webpackChunkName: "my-schedule" */ '../views/MySchedule.vue'),
        meta: {
            noFooter: true,
        },
    },
    {
        path: '/my-appointments/',
        name: 'Meine Termine',
        component: () => import(/* webpackChunkName: "my-appointments" */ '../views/MyAppointments.vue'),
    },
    {
        path: '/payoff/',
        name: 'Abrechnung',
        component: () => import(/* webpackChunkName: "payoff" */ '../views/Payoff.vue'),
    },
    {
        path: '/payoff-audit',
        name: 'Rechnungsprüfung',
        component: () => import(/* webpackChunkName: "payoff" */ '../views/PayoffAudit.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.PAYOFF_AUDIT]},
    },
    {
        path: '/courses',
        name: 'Kurse',
        component: () => import(/* webpackChunkName: "all-courses" */ '../views/CoursesView.vue'),
    },
    {
        path: '/courses/:courseId',
        name: 'Kurs',
        component: () => import(/* webpackChunkName: "course" */ '../views/CourseView.vue'),
    },
    // Login without any role / menu key: only this page (and the public / answer pages), no office shell
    {
        path: NO_ACCESS_HOME,
        name: 'Noch nichts freigeschaltet',
        component: () => import(/* webpackChunkName: "no-access" */ '@/views/NoAccessView.vue'),
        meta: {forAll: true, noFooter: true},
    },
    // Vertretungsplan is a tab of "Vertretungen" (/substitution-inquiries-table?tab=plan)
    {path: '/substitution-plan', redirect: {path: '/substitution-inquiries-table', query: {tab: 'plan'}}},
    {
        path: '/employees',
        name: 'Mitarbeitende',
        component: () => import(/* webpackChunkName: "employees" */ '../views/EmployeesView.vue'),
    },
    {
        path: '/teachers',
        name: 'Lehrkräfte',
        component: () => import(/* webpackChunkName: "teachers" */ '../views/TeachersView.vue'),
    },
    {
        path: '/tools/metaAds',
        name: 'Werbeanzeigen (Meta)',
        component: () => import(/* webpackChunkName: "meta-ads" */ '../views/tools/MetaAdsView.vue'),
    },
    {
        path: '/tools/missingDocuments',
        name: 'Fehlende Dokumente',
        component: () => import(/* webpackChunkName: "missing-documents" */ '../views/MissingDocuments.vue'),
    },
    {
        path: '/tools/surveys',
        name: 'Umfragen',
        component: () => import(/* webpackChunkName: "employee-documents" */ '../views/tools/Surveys.vue'),
    },
    {
        path: '/tools/surveys/:surveyId',
        name: 'Umfrageergebnisse',
        component: () => import(/* webpackChunkName: "employee" */ '../views/SurveyResponse.vue'),
    },
    {
        path: '/tools/workflows',
        name: 'Workflows',
        component: () => import(/* webpackChunkName: "employee-documents" */ '../views/tools/WorkflowsView.vue'),
    },
    // The early warning lives in the QM-Cockpit
    {path: '/tools/workflows/early-warning', redirect: {path: '/quality-management', query: {tab: 'earlyWarning'}}},
    ...EMPLOYEE_ROUTES,
    {
        path: '/students',
        name: 'Alle Schüler',
        component: () => import(/* webpackChunkName: "students" */ '@/views/StudentsView.vue'),
    },
    {
        path: '/students/:studentId',
        name: 'Schüler',
        component: () => import(/* webpackChunkName: "student" */ '@/views/StudentView.vue'),
        // ?tab= full views are history entries (ProfileTabView.mixin); don't remount the page for them
        meta: {keepOnQueryChange: true},
    }, {
        // Previous profile for comparison until the new profile covers every function (then delete with StudentViewLegacy.vue)
        path: '/students-legacy/:studentId',
        name: 'Schüler (alt)',
        component: () => import(/* webpackChunkName: "student-legacy" */ '@/views/StudentViewLegacy.vue'),
    }, {
        path: '/parents',
        name: 'Alle Eltern',
        component: () => import(/* webpackChunkName: "parents" */ '@/views/ParentsView.vue'),
    },
    {
        path: '/parents/:parentId',
        name: 'Eltern',
        component: () => import(/* webpackChunkName: "parent" */ '../views/ParentView.vue'),
        meta: {keepOnQueryChange: true},
    },
    {
        // Previous profile for comparison until the new profile covers every function (then delete with ParentViewLegacy.vue)
        path: '/parents-legacy/:parentId',
        name: 'Eltern (alt)',
        component: () => import(/* webpackChunkName: "parent-legacy" */ '../views/ParentViewLegacy.vue'),
    },
    {
        path: '/tools/bookInvoiceGenerator',
        name: 'Buchrechnungsgenerator',
        component: () => import(/* webpackChunkName: "book-invoices" */ '../views/tools/BookInvoiceGenerator.vue'),
    },
    {
        path: '/tools/sepaTools',
        name: 'SEPA-Aufteilung',
        component: () => import(/* webpackChunkName: "sepa-xml" */ '../views/tools/SepaTools.vue'),
    },
    {
        path: '/tools/migration',
        name: 'Migration',
        component: () => import(/* webpackChunkName: "migration" */ '../views/tools/Migration.vue'),
    },
    {
        path: '/tools/migration/employees',
        name: 'Lehrkräfte und Mitarbeitende migrieren',
        component: () => import(/* webpackChunkName: "migration-employees" */ '../views/tools/migration/EmployeeMigration.vue'),
    },
    {
        path: '/tools/placetelTestProfile',
        name: 'Placetel-Testprofil',
        component: () => import(/* webpackChunkName: "placetel-test-profile" */ '../views/tools/PlacetelTestProfile.vue'),
    },
    {
        path: '/tools/placetelCallCenterSchedule',
        name: 'Call-Center-Dienstplan',
        component: () => import(/* webpackChunkName: "placetel-call-center-schedule" */ '../views/tools/PlacetelCallCenterSchedule.vue'),
    },
    {
        path: '/tools/tags',
        name: 'Tag Manager',
        component: () => import(/* webpackChunkName: "tags" */ '../views/tools/TagManager.vue'),
    },
    {
        path: '/about',
        name: 'About',
        component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
    },
    {
        path: '/activity-slot-subscription/confirm',
        component: () => import(/* webpackChunkName: "about" */ '../views/activitySlotSubscription/ConfirmActivitySlotSubscriptionView.vue'),
        meta: {
            forAll: true,
            hideNavbar: true,
        },
    },
    {
        path: '/activity-slot-subscription/unsubscribe',
        component: () => import(/* webpackChunkName: "about" */ '../views/activitySlotSubscription/UnsubscribeActivitySlotSubscriptionView.vue'),
        meta: {
            forAll: true,
            hideNavbar: true,
        },
    },
    {
        path: '/login',
        component: LoginView,
        meta: {
            guestOnly: true,
        },
    },
    ...AUTH_ROUTES,
    {
        path: '/onboarding',
        component: () => import (/* webpackChunkName: "onboarding" */ '../views/OnboardingView.vue'),
        meta: {
            guestOnly: true,
        },
    },
    {
        path: '/trackBookingState/:submissionId',
        name: 'Buchungsstatus',
        component: () => import(/* webpackChunkName: "track-booking-state" */ '../views/bookingForm/TrackBookingState.vue'),
        meta: {
            forAll: true,
            hideNavbar: true,
        },
    },
    /** LEGACY **/
    {
        path: '/code',
        name: 'Anmeldung per Buchungscode',
        redirect: '/anmelden',
    },
    {
        path: '/form/:shortId',
        name: 'Online-Anmeldung',
        redirect: '/anmelden',
    },
    ...STATS_ROUTES,
    {
        path: '/planning/handout-events',
        name: 'Austeilaktionen',
        component: () => import(/* webpackChunkName: "handout-events" */ '@/views/planning/HandoutEvents.vue'),
    },
    {
        path: '/planning/handout-rounds',
        name: 'Austeilrunden',
        component: () => import(/* webpackChunkName: "handout-rounds" */ '@/views/planning/HandoutRounds.vue'),
    },
    {
        path: '/planning/handout-rounds/:roundId',
        name: 'Austeilrunde',
        component: () => import(/* webpackChunkName: "handout-rounds" */ '@/views/planning/HandoutRounds.vue'),
    },
    {
        path: '/planning/handout-events-feedback',
        name: 'Austeilaktionen - Feedback',
        component: () => import(/* webpackChunkName: "handout-events" */ '@/views/planning/HandoutEventsFeedback.vue'),
    },
    {
        path: '/planning/handout-events/:handoutEventId',
        name: 'Austeilaktion',
        component: () => import(/* webpackChunkName: "handout-event" */ '@/views/planning/HandoutEvent.vue'),
    },
    {
        path: '/planning/handout-events/advertiser-feedback/:handoutEventId',
        name: 'Feedback zur Austeilaktion - Werbende:r',
        component: () => import(/* webpackChunkName: "handout-event" */ '@/views/planning/HandoutEventAdvertiserFeedback.vue'),
        meta: {
            forAll: true,
            answerPage: true,
            // removing ?t= (signed link token) from the URL must not re-create the view (it would lose the token)
            keepOnQueryChange: true,
        },
    },
    {
        path: '/planning/handout-events/institution-feedback/:handoutEventId',
        name: 'Feedback zur Austeilaktion - Standort',
        component: () => import(/* webpackChunkName: "handout-event" */ '@/views/planning/HandoutEventInstitutionFeedback.vue'),
        meta: {
            forAll: true,
            answerPage: true,
            // removing ?t= (signed link token) from the URL must not re-create the view (it would lose the token)
            keepOnQueryChange: true,
        },
    },
    {
        path: '/handout-date-request/:handoutEventId',
        name: 'Terminwahl der Austeilaktion - Schule',
        component: () => import(/* webpackChunkName: "handout-event" */ '@/views/planning/HandoutDateRequest.vue'),
        meta: {
            forAll: true,
            answerPage: true,
            // removing ?t= (signed link token) from the URL must not re-create the view (it would lose the token)
            keepOnQueryChange: true,
        },
    },
    {
        path: '/planning/handout-overview',
        name: 'Terminübersicht für Austeilaktionen',
        component: () => import(/* webpackChunkName: "handout-overview" */ '@/views/planning/HandoutOverview.vue'),
    },
    {
        path: '/planning/open-institutions',
        name: 'Offene Standorte',
        component: () => import(/* webpackChunkName: "open-institutions" */ '@/views/planning/OpenInstitutionsView.vue'),
    },
    {
        path: '/vapi',
        name: 'Vapi',
        component: () => import(/* webpackChunkName: "handout-overview" */ '@/views/VapiView.vue'),
    },
    {
        path: '/vapi/assistants',
        name: 'Vapi Assistenten',
        component: () => import(/* webpackChunkName: "handout-overview" */ '@/views/VapiAssistantsView.vue'),
    },
    {
        path: '/vapi/calls',
        name: 'Vapi Anrufe',
        component: () => import(/* webpackChunkName: "handout-overview" */ '@/views/VapiCallsView.vue'),
    },
    ...SETTINGS_ROUTES,
    {
        path: '/messages',
        name: 'Nachrichten',
        component: () => import(/* webpackChunkName: "messages" */ '@/views/MessagesView.vue'),
        meta: {keepOnQueryChange: true, noFooter: true},
    },
    {
        path: '/warehouse/shipping',
        name: 'Versand',
        component: () => import(/* webpackChunkName: "shipping" */ '../views/warehouse/ShippingView.vue'),
        meta: {keepOnQueryChange: true},
    },
    {
        path: '/warehouse/orders',
        name: 'Bestellungen',
        component: () => import(/* webpackChunkName: "products" */ '../views/warehouse/OrdersView.vue'),
    },
    // Former shipment list, now the "Sendungen" tab of Versand
    {path: '/warehouse/shipments', redirect: {path: '/warehouse/shipping', query: {tab: 'shipments'}}},
    {
        path: '/warehouse/scheduled-product-series',
        name: 'Serienversand-Abos',
        component: () => import( '../views/warehouse/ScheduledProductSeriesSubscriptionsView.vue'),
    },
    {
        path: '/warehouse/shipment-bundles',
        name: 'Versandpakete',
        component: () => import( '../views/warehouse/ShipmentsBundleView.vue'),
    },
    {
        path: '/templates/invoice',
        name: 'Rechnungsvorlagen',
        component: () => import(/* webpackChunkName: "invoice-templates" */ '../views/templates/InvoiceTemplates.vue'),
    },
    {
        path: '/templates/invoice/:id',
        name: 'Rechnungsvorlage',
        component: () => import(/* webpackChunkName: "invoice-template" */ '../views/templates/InvoiceTemplate.vue'),
    },
    {
        path: '/templates/contract-templates',
        name: 'Vergütungsvorlagen',
        component: () => import(/* webpackChunkName: "contracts" */ '../views/EmployeeContractTemplatesView.vue'),
    },
    {
        path: '/templates/mail',
        name: 'E-Mail Templates',
        component: () => import(/* webpackChunkName: "mail-templates" */ '../views/templates/MailTemplates.vue'),
    },
    {
        path: '/templates/mail/:templateId/:templateType',
        name: 'E-Mail Template',
        component: () => import(/* webpackChunkName: "mail-template" */ '../views/templates/MailEditor.vue'),
    },
    {
        path: '/templates/whatsapp/:configId/edit/:templateId?',
        name: 'WhatsApp Template',
        component: () => import(/* webpackChunkName: "whatsapp-template" */ '../views/templates/WhatsAppTemplateEditor.vue'),
    },
    {
        path: '/templates/whatsapp/:id?',
        name: 'WhatsApp Templates',
        component: () => import(/* webpackChunkName: "whatsapp-templates" */ '../views/templates/WhatsAppTemplates.vue'),
    },
    {
        path: '/templates/handout',
        name: 'Anmeldungsvorlagen',
        component: () => import(/* webpackChunkName: "handout-templates" */ '@/views/templates/HandoutTemplates.vue'),
    },
    {
        path: '/institutions',
        name: 'Standorte',
        component: () => import(/* webpackChunkName: "institutions" */ '@/views/InstitutionsView.vue'),
    },
    {
        path: '/institutions/:institutionId',
        name: 'Standort',
        component: () => import(/* webpackChunkName: "institution" */ '../views/InstitutionView.vue'),
        meta: {keepOnQueryChange: true},
    },
    {
        // Previous profile for comparison until the new profile covers every function (then delete with InstitutionViewLegacy.vue)
        path: '/institutions-legacy/:institutionId',
        name: 'Standort (alt)',
        component: () => import(/* webpackChunkName: "institution-legacy" */ '../views/InstitutionViewLegacy.vue'),
    },
    {
        path: '/coursematerials',
        name: 'Kursmaterialien',
        component: () => import (/* webpackChunkName: "course-materials" */ '../views/CourseMaterials.vue'),
    },
    {
        path: '/subjects',
        name: 'Fächer',
        component: () => import(/* webpackChunkName: "subjects" */ '../views/SubjectsView.vue'),
    },
    {
        path: '/vacation',
        name: 'Ferien & Feiertage',
        component: () => import(/* webpackChunkName: "vacation" */ '../views/Vacation.vue'),
    },
    {
        path: '/taxonomies',
        name: 'Taxonomien',
        component: () => import(/* webpackChunkName: "taxonomies" */ '../views/TaxonomiesView.vue'),
    },
    {
        path: '/booking',
        name: 'Vorlagen für Anmeldungen',
        component: () => import(/* webpackChunkName: "booking-form-templates" */ '../views/bookingForm/BookingFormTemplates.vue'),
    },
    {
        path: '/booking/:id',
        name: 'Anmeldungsformular',
        component: () => import(/* webpackChunkName: "booking-form-template" */ '../views/bookingForm/BookingFormTemplateView.vue'),
    },
    {
        path: '/booking/onlineFormBuilder/new/:templateId',
        name: 'Digitales Formular erzeugen',
        component: () => import(/* webpackChunkName: "new-digital-form" */ '../views/OnlineFormBuilder.vue'),
    },
    {
        path: '/booking/onlineFormBuilder/:templateId/:id',
        name: 'Digitales Formular bearbeiten',
        component: () => import(/* webpackChunkName: "edit-digital-form" */ '../views/OnlineFormBuilder.vue'),
    },
    {
        path: '/booking/onlineFormBuilderPreview/:formId',
        name: 'Online-Formular Vorschau',
        component: () => import(/* webpackChunkName: "form-preview" */ '../views/bookingForm/BookingFormBuilderPreview.vue'),
    },
    {
        path: '/booking/printedFormBuilder/new/:type/:templateId',
        name: 'Formular erstellen',
        component: () => import(/* webpackChunkName: "new-printed-form" */ '../views/bookingForm/PrintedFormBuilder.vue'),
    },
    {
        path: '/booking/printedFormBuilder/:type/:templateId/:id',
        name: 'Formular bearbeiten',
        component: () => import(/* webpackChunkName: "edit-printed-form" */ '../views/bookingForm/PrintedFormBuilder.vue'),
    },
    {
        path: '/trainings',
        name: 'Schulungen',
        component: () => import(/* webpackChunkName: "trainings" */ '../views/Trainings.vue'),
    },
    {
        path: '/trainings/:trainingId',
        name: 'Schulung',
        component: () => import(/* webpackChunkName: "training" */ '../views/TrainingItem.vue'),
    },
    {
        path: '/signup/:id',
        name: 'Registrierung abschließen',
        component: () => import (/* webpackChunkName: "signup" */ '../views/Signup.vue'),
        meta: {
            guestOnly: true,
        },
    },
    {
        path: '/test-link',
        name: 'Test-Link',
        component: () => import (/* webpackChunkName: "whatsapp-test-link" */ '../views/WhatsAppTestLinkView.vue'),
        meta: {
            forAll: true,
            answerPage: true,
        },
    },
    {
        path: '/handout-event-confirmation/:token',
        name: 'Terminbestätigung für Austeilaktion',
        component: () => import (/* webpackChunkName: "handout-event-confirmation" */ '../views/HandoutEventConfirmation.vue'),
        meta: {
            forAll: true,
            answerPage: true,
        },
    },
    {
        path: '/imprint',
        name: 'Impressum',
        component: () => import(/* webpackChunkName: "imprint" */ '../views/legal/Imprint.vue'),
        meta: {
            forAll: true,
        },
    },
    {
        path: '/compliance',
        name: 'Datenschutz',
        component: () => import(/* webpackChunkName: "compliance" */ '../views/legal/Compliance.vue'),
        meta: {
            forAll: true,
        },
    },
    {
        path: '/recruiter/jobAds',
        name: 'Stellenanzeigen',
        component: () => import (/* webpackChunkName: "recruiter" */ '../views/recruiter/JobAds.vue'),
    },
    {
        path: '/recruiter/jobAds/:jobAdId',
        name: 'Stellenanzeige',
        component: () => import (/* webpackChunkName: "recruiter" */ '../views/recruiter/JobAds.vue'),
    },
    {
        path: '/recruiter/applicants',
        name: 'Bewerber:innen',
        component: () => import (/* webpackChunkName: "recruiter" */ '../views/recruiter/ApplicantsView.vue'),
    },
    {
        path: '/recruiter/applicants/:employeeId',
        name: 'Bewerber:in (Pipeline)',
        component: () => import (/* webpackChunkName: "recruiter" */ '../views/recruiter/ApplicantPipelineItem.vue'),
        // ?cleanup=1 wird nach dem Öffnen von „Aufräumen …“ entfernt – ohne das hier baute App.vue die Seite neu auf (Dialog weg)
        meta: {keepOnQueryChange: true},
    },
    {
        path: '/recruiter/landingpage',
        name: 'Bewerber:innen',
        component: () => import (/* webpackChunkName: "recruiter" */ '../views/recruiter/ApplicantsLandingPageOverview.vue'),
    },
    ...ACQUISITION_ROUTES,
    // Optimizations are hidden (outdated optimizer); old links lead to the start page
    OPTIMIZATIONS_ENABLED
        ? {
            path: '/optimizations',
            name: 'Optimierungen',
            component: () => import(/* webpackChunkName: "optimizations" */ '@/views/Optimizations.vue'),
        }
        : {
            path: '/optimizations',
            redirect: '/',
        },
    {
        path: '/quality-management',
        name: 'QM-Cockpit',
        component: () => import(/* webpackChunkName: "quality-cockpit" */ '@/views/qualityManagement/QualityCockpitView.vue'),
        meta: {
            // the old keys imply QUALITY_MANAGEMENT_COCKPIT (getEffectiveAccess); TOOLS_WORKFLOWS for the workflow links to the early warning
            guiAccess: [
                NavigationDrawerAccess.QUALITY_MANAGEMENT_COCKPIT,
                NavigationDrawerAccess.TOOLS_WORKFLOWS,
            ],
        },
    },
    // Replaced by the QM-Cockpit
    {path: '/qualityManagement/teachers', redirect: {path: '/quality-management', query: {tab: 'people'}}},
    {path: '/qualityManagement/institutions', redirect: {path: '/quality-management', query: {tab: 'people'}}},
    ...takeoverOfferRoutes,
    ...makeupRoundRoutes,
    ...qualityFeedbackRoutes,
    ...applicantDeletionRoutes,
    ...TEACHER_PORTAL_ROUTES,
    ...APPLICANT_PORTAL_ROUTES,
    {
        path: '/substitution-inquiries-table',
        name: 'Vertretungen',
        component: () => import('@/views/SubstitutionInquiriesTableView.vue'),
        // "Anfragen" (SUBSTITUTION_INQUIRIES_TABLE) or the Vertretungsplan tab (SUBSTITUTION_PLAN, former /substitution-plan)
        meta: {guiAccess: [NavigationDrawerAccess.SUBSTITUTION_INQUIRIES_TABLE, NavigationDrawerAccess.SUBSTITUTION_PLAN]},
    },
    {
        path: '/todos/:todoId?',
        name: 'ToDos',
        component: () => import('@/views/ToDos.vue'),
    },
    {
        path: '/catalogue/products',
        name: 'Produkte',
        component: () => import( '@/views/ProductsView.vue'),
    },
    {
        path: '/accounting/invoice-cycles',
        name: 'Rechnungsläufe',
        component: () => import('@/views/accountant/InvoiceCycleView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.INVOICE_CYCLES]},
    },
    {
        path: '/accounting/invoices',
        name: 'Ausgangsrechnungen',
        component: () => import('@/views/accountant/InvoiceView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.INVOICES]},
    },
    {
        path: '/accounting/refunds',
        name: 'Rückerstattungen',
        component: () => import('@/views/accountant/MakeupRefundsView.vue'),
        // ?month / ?status / ?way change with every tab: keep the view (no reload, the action button stays)
        meta: {guiAccess: [NavigationDrawerAccess.MAKEUP_REFUNDS], keepOnQueryChange: true},
    },
    {
        path: '/accounting/room-rent',
        name: 'Raummiete',
        component: () => import(/* webpackChunkName: "room-rent" */ '@/views/accountant/RoomRentView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.ROOM_RENT]},
    },
    {
        path: '/catalogue/scheduled-product-series',
        name: 'Produkte',
        component: () => import( '@/views/ScheduledProductSeriesView.vue'),
    },
    {
        path: '/activities',
        name: 'Aktivitäten',
        component: () => import( '@/views/ActivitiesView.vue'),
    },
    {
        path: '/activity-registrations',
        name: 'Anmeldungen',
        component: () => import( '@/views/ActivityRegistrationsView.vue'),
    },
    {
        path: '/anmelden',
        name: 'Anmeldung',
        component: () => import( '@/views/ActivitySignupView.vue'),
        meta: {
            forAll: true,
            hideNavbar: true,
        },
    },
    {
        path: '/bewerben',
        name: 'Bewerbung',
        component: () => import( '@/views/ApplicantsLandingPageView.vue'),
        meta: {
            forAll: true,
            hideNavbar: true,
        },
    },
    {
        path: '/substitution-inquiries/:employeeId?',
        name: 'Mögliche Vertretungen',
        component: () => import('@/views/SubstitutionInquiriesView.vue'),
        meta: {
            forAll: true,
            answerPage: true,
            // removing ?t= from the URL must not re-create the view (it would lose the link token)
            keepOnQueryChange: true,
        },
    },
    {
        path: '/partner',
        name: 'Heute',
        component: () => import(/* webpackChunkName: "partner" */ '@/views/partner/PartnerTodayView.vue'),
        meta: {partner: true, guiAccess: [NavigationDrawerAccess.PARTNER_TODAY]},
    },
    {
        path: '/partner/pipeline',
        name: 'Pipeline',
        component: () => import(/* webpackChunkName: "partner" */ '@/views/partner/PartnerPipelineView.vue'),
        meta: {partner: true, guiAccess: [NavigationDrawerAccess.PARTNER_PIPELINE]},
    },
    {
        path: '/partner/call/:institutionId?',
        name: 'Telefonieren',
        component: () => import(/* webpackChunkName: "partner" */ '@/views/partner/PartnerCallView.vue'),
        meta: {partner: true, guiAccess: [NavigationDrawerAccess.PARTNER_CALL]},
    },
    {
        path: '/partner/appointments',
        name: 'Termine',
        component: () => import(/* webpackChunkName: "partner" */ '@/views/partner/PartnerAppointmentsView.vue'),
        meta: {partner: true, guiAccess: [NavigationDrawerAccess.PARTNER_APPOINTMENTS]},
    },
    {
        path: '/partner/stats',
        name: 'Leistung',
        component: () => import(/* webpackChunkName: "partner" */ '@/views/partner/PartnerStatsView.vue'),
        meta: {partner: true, guiAccess: [NavigationDrawerAccess.PARTNER_STATS]},
    },
    {
        path: '/acquisitionBoard',
        name: 'Akquise-Board',
        component: () => import(/* webpackChunkName: "sales-partner" */ '@/views/salesPartner/AcquisitionBoardView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.ACQUISITION_BOARD]},
    },
    {
        path: '/salesAppointments',
        name: 'Folgegespräche',
        component: () => import(/* webpackChunkName: "sales-partner" */ '@/views/salesPartner/SalesAppointmentsView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.SALES_APPOINTMENTS]},
    },
    {
        path: '/salesPartner/stats',
        name: 'Auswertung Agentur',
        component: () => import(/* webpackChunkName: "sales-partner" */ '@/views/salesPartner/SalesPartnerStatsView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.SALES_PARTNER_STATS]},
    },
    {
        path: '/salesPartner/changes',
        name: 'Agentur-Änderungen',
        component: () => import(/* webpackChunkName: "sales-partner" */ '@/views/salesPartner/SalesPartnerChangesView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.SALES_PARTNER_CHANGES]},
    },
    {
        path: '/salesPartner/settings',
        name: 'Agentur verwalten',
        component: () => import(/* webpackChunkName: "sales-partner" */ '@/views/salesPartner/SalesPartnerSettingsView.vue'),
        meta: {guiAccess: [NavigationDrawerAccess.SALES_PARTNER_SETTINGS]},
    },
    {
        path: '*',
        name: '404',
        component: MissingPageView,
        meta: {
            forAll: true,
        },
    },
];

const router = new VueRouter({
    routes,
});

/**
 * Continues navigation or redirects if the user may not open the route (sales partner area, navigation permissions)
 */
function nextWithAccessCheck(to: Route, next: NavigationGuardNext) {
    // First check after an account switch: the page of the previous account is left silently (notice of the switch instead)
    const accountSwitch = consumeAccountSwitch();
    if (accountSwitch?.notice) {
        store.commit('showSnackbar', {text: accountSwitch.notice, btnColor: 'success'});
    }
    const access = resolveRouteAccess(to);
    if (access === true) {
        next();
        return;
    }
    // The start page simply leads partners to their own area, no hint needed there; teachers and applicants are led to
    // their area without a hint, too (no snackbars in the Lehrkraft-Bereich)
    if (!accountSwitch && to.path !== '/' && !isTeacherPortalPath(access) && !isApplicantPortalPath(access) && !isNoAccessPath(access)) {
        store.commit('showSnackbar', {
            text: 'Diese Seite ist für dich nicht freigegeben.',
            btnColor: 'warning',
        });
    }
    next(access);
}

let loadingTimer: ReturnType<typeof setTimeout> | undefined;

/** Shows the loading bar if the navigation (guards, loading the view's chunk) takes longer than 200ms */
function startLoading() {
    clearTimeout(loadingTimer);
    loadingTimer = setTimeout(() => store.commit('setLoadingState', true), 200);
}

function stopLoading() {
    clearTimeout(loadingTimer);
    loadingTimer = undefined;
    if (store.state.loading) {
        store.commit('setLoadingState', false);
    }
}

router.beforeEach(async (to, from, guardNext) => {
    startLoading();
    // Aborts and redirects never reach afterEach (redirects start a new navigation with its own loading state)
    const next = ((target?: Parameters<NavigationGuardNext>[0]) => {
        if (target !== undefined) {
            stopLoading();
        }
        guardNext(target);
    }) as NavigationGuardNext;
    try {
        const pixelId = getPixelIdForPath(to.path);
        if (pixelId) {
            loadMetaPixel(pixelId);
        }
    } catch (e) {
        // do nothing
    }
    if (!store.state.axios && to.path !== '/onboarding') {
        try {
            const canInitialize = await InitializationApi.canInitialize();
            if (canInitialize) {
                next('/onboarding');
                return;
            }
        } catch (e) {
            // DO NOTHING
        }
    }
    // Buttons of WhatsApp test messages carry the example values of the template: explain instead of "Link ungültig"
    const testLinkPage = to.name !== 'Test-Link' ? testLinkTarget(to.path) : undefined;
    if (testLinkPage) {
        next({path: '/test-link', query: {page: testLinkPage}});
        return;
    }
    if (to.meta?.guestOnly && store.state.axios) {
        if (store.state.routeCache) {
            router.push(store.state.routeCache.fullPath).catch(() => undefined);
            store.commit('setRouteCache', null);
        } else {
            router.push('/').catch(() => undefined);
        }
    } else if (!to.meta || !to.meta.guestOnly && !store.state.axios) {
        const forAll = !!to.meta?.forAll;

        try {
            let resp: CheckLoginResponseDto | undefined;
            try {
                await ClientHooks.preLogin();
            } catch (e) {
                store.commit('showSnackbar', {
                    text: 'Fehler beim Ausführen eines System-Hooks (Pre-Login)',
                    btnColor: 'error',
                    info: isAxiosError(e) ? `${e.status || e.code || ''} ${e.message}`.trim() : e,
                });
            }
            try {
                resp = await LoginApi.checkLogin();
            } catch (err) {
                if (!forAll) {
                    store.commit('setRouteCache', to);
                }
            }
            if (resp) {
                store.commit('login');
                store.commit('user', resp.meta);
                store.commit('setUserType', resp.loginType);
                store.commit('setOriginUserId', resp.originUserId);
                store.commit('setViewAsRole', resp.viewAsRole || null);
                store.commit('setLoginUserId', resp.loginUserId);
                try {
                    await ClientHooks.postLogin();
                } catch (e) {
                    store.commit('showSnackbar', {
                        text: 'Fehler beim Ausführen eines System-Hooks (Post-Login)',
                        btnColor: 'error',
                        info: isAxiosError(e) ? `${e.status || e.code || ''} ${e.message}`.trim() : e,
                    });
                }
                await LoginApi.setCrud();
                store.commit('setAppReady', true);
                await NotifierSocketApi.connect();
                nextWithAccessCheck(to, next);
            } else {
                if (forAll) {
                    next();
                } else {
                    next('/login');
                }
            }
        } catch (error) {
            if (forAll) {
                next();
            } else {
                next('/login');
            }
        }
    } else {
        // Profile full views (?tab=) are history entries of the same page: keep its title and context actions
        // (the title is set after the navigation, by the route that is really opened – see afterEach)
        if (!(to.meta?.keepOnQueryChange && to.path === from.path)) {
            store.commit('hideActions');
        }
        if (store.state.axios) {
            try {
                // Only awaited if the access is not known yet, otherwise it is refreshed in the background
                await LoginApi.ensureCrud();
            } catch (e) {
                // Without the access the check would deny every page, so the navigation is cancelled and can be retried
                store.commit('showSnackbar', {
                    text: 'Berechtigungen konnten nicht geladen werden. Bitte erneut versuchen.',
                    btnColor: 'error',
                    info: isAxiosError(e) ? `${e.status || e.code || ''} ${e.message}`.trim() : e,
                });
                next(false);
                return;
            }
            store.commit('setAppReady', true);
            nextWithAccessCheck(to, next);
        } else {
            next();
        }
    }
});

router.afterEach(() => stopLoading());

// Title of the opened route – also after silent redirects (/my-application or / → /my-teaching/today) and on the first load
router.afterEach((to, from) => {
    const title = routeTitle(to, from);
    if (title !== null) {
        store.commit('setTitle', title);
    }
});

// Search engines: noindex for every page except the login
router.afterEach((to) => applyRobotsMeta(to.path));
router.onError(() => stopLoading());

// Remembers visited profiles for "Zuletzt besucht" of the search palette
router.afterEach((to) => trackRecentVisit(to));

// router.afterEach((to) => {
//     const pixelId = getPixelIdForPath(to.path);
//     if (pixelId) {
//         loadMetaPixel(pixelId);
//     }
// });

export default router;
